import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import PropertyForm from "@/components/PropertyForm";

const lookingFor = [
  { title: "Development Opportunities", text: "Properties that may support residential, infill, redevelopment or other forms of development." },
  { title: "Strategic Properties", text: "Properties within or adjacent to towns, villages, settlement areas or communities experiencing growth." },
  { title: "Large Parcels", text: "Properties with sufficient size, access and overall potential to investigate a development project." },
  { title: "Serviced or Serviceable Locations", text: "Properties where the infrastructure required for development may be available or achievable." },
  { title: "Redevelopment & Infill", text: "Underutilized properties in established areas where improvement or redevelopment may create new value." },
  { title: "Investment Properties", text: "Real estate with income or value-add potential that may align with a long-term strategy." },
];

const howItWorks = [
  {
    num: "01",
    title: "Property Review",
    text: "Stelcore conducts an initial review of the property and its surrounding context.",
    points: ["Location and property size", "Road access and road frontage", "Surrounding development and existing land use", "Infrastructure and community growth", "Major environmental constraints", "General development potential"],
  },
  {
    num: "02",
    title: "Feasibility",
    text: "If the property appears promising, Stelcore investigates whether development may be practical. The review may involve appropriate professionals and consideration of the requirements that could affect the project.",
  },
  {
    num: "03",
    title: "Development Concept",
    text: "Where appropriate, a preliminary development concept may be explored.",
    points: ["Potential site layout and building program", "Access and infrastructure", "Future connections and stormwater considerations", "Development phases and preliminary yield"],
    note: "Preliminary concepts are not approved plans.",
  },
  {
    num: "04",
    title: "Investment Structure",
    text: "Where appropriate, Stelcore and the property owner may explore a structure where the owner contributes their property to the development opportunity and participates alongside Stelcore.",
    points: ["Property value and project economics", "Capital requirements and financing", "Risk and development timeline", "Tax considerations and legal advice", "Objectives of both parties"],
    note: "No single structure is universally applicable; each arrangement depends on the property and the parties.",
  },
  {
    num: "05",
    title: "Development",
    text: "If the property proceeds, Stelcore helps coordinate the professionals and development process necessary to advance the project.",
  },
  {
    num: "06",
    title: "Value Creation",
    text: "The objective is to move suitable properties toward defined development outcomes and create value through the development process. Final approval, profitability and timing cannot be guaranteed.",
  },
];

const moreThanSale = [
  { title: "Participate in the Development Opportunity", text: "A traditional sale generally transfers the property's future development potential to the buyer. For certain properties, a structured development arrangement may allow the existing owner to remain involved in the development opportunity." },
  { title: "Development Process", text: "Taking a property from its current state through the development process can involve significant coordination, professional work and time. Stelcore helps manage and coordinate that process." },
  { title: "Reduced Management Burden", text: "The property owner may not want to personally coordinate the many professionals and requirements involved with a development project." },
  { title: "Aligned Interests", text: "A properly structured arrangement may allow Stelcore and the property owner to work toward advancing the same underlying property." },
];

const indicators = [
  "Sufficient size or scale",
  "Located within or near a growing community",
  "Within or near a settlement area",
  "Beside existing development",
  "Good road access",
  "Municipal infrastructure nearby",
  "Logical location for future growth",
  "Reasonably developable terrain",
  "Clear development or value-add potential",
  "Potential future road connections",
  "Surrounding development activity",
];

const InvestWithUs = () => {
  const { hash } = useLocation();

  useEffect(() => {
    document.title = "Invest With Us | Stelcore Development";
  }, []);

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [hash]);

  return (
    <div className="bg-ink text-bone" data-testid="invest-page">
      {/* HERO */}
      <section className="border-b border-bone/10">
        <div className="container-x py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionLabel dark>Invest With Us</SectionLabel>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold uppercase mt-6 mb-6" data-testid="invest-headline">
                Have a Property Opportunity?
              </h1>
              <p className="text-lg text-bone/70 mb-8">Stelcore Development is actively evaluating real estate opportunities.</p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <p className="text-sm text-bone/60 leading-relaxed mb-6">
                If you own a property and are considering selling, or believe your property may have
                development or investment potential, we would be interested in hearing from you.
              </p>
              <p className="text-sm text-bone/60 leading-relaxed mb-10">
                For suitable properties, Stelcore may consider working with the owner through a
                structured arrangement where the property becomes part of a development opportunity —
                and where appropriate, the owner may participate alongside Stelcore as the project advances.
              </p>
              <a href="#submit" className="btn-light" data-testid="invest-hero-cta">
                Submit Your Property <ArrowRight size={14} />
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHAT WE ARE LOOKING FOR */}
      <section className="border-b border-bone/10" data-testid="looking-for-section">
        <div className="container-x py-20 md:py-28">
          <Reveal>
            <SectionLabel dark>What We Are Looking For</SectionLabel>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold uppercase mt-6 mb-14">
              Properties with potential.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-bone/10">
            {lookingFor.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06} className="border-b border-r border-bone/10">
                <div className="p-8 h-full" data-testid={`looking-for-card-${i + 1}`}>
                  <p className="text-xs tracking-[0.25em] text-taupe font-semibold mb-5">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="text-base font-semibold uppercase tracking-tight mb-4">{c.title}</h3>
                  <p className="text-sm text-bone/60 leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="text-xs text-bone/40 mt-8 max-w-2xl leading-relaxed">
              These characteristics describe the types of properties Stelcore reviews. They do not imply
              that any property will be accepted or that development approvals can be obtained.
            </p>
          </Reveal>
        </div>
      </section>

      {/* HOW IT COULD WORK */}
      <section id="how-it-works" className="border-b border-bone/10 scroll-mt-24" data-testid="how-it-works-section">
        <div className="container-x py-20 md:py-28">
          <Reveal>
            <SectionLabel dark>How It Could Work</SectionLabel>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold uppercase mt-6 mb-14">
              A clear path, step by step.
            </h2>
          </Reveal>
          <div className="space-y-0 border-t border-bone/10">
            {howItWorks.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.05}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 border-b border-bone/10 py-10" data-testid={`how-step-${s.num}`}>
                  <div className="md:col-span-2">
                    <span className="text-2xl font-heading font-semibold text-taupe">{s.num}</span>
                  </div>
                  <div className="md:col-span-4">
                    <h3 className="text-xl font-semibold uppercase tracking-tight">{s.title}</h3>
                  </div>
                  <div className="md:col-span-6">
                    <p className="text-sm text-bone/60 leading-relaxed">{s.text}</p>
                    {s.points && (
                      <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                        {s.points.map((pt) => (
                          <li key={pt} className="flex items-start gap-2 text-sm text-bone/70">
                            <Check size={14} className="text-taupe mt-0.5 shrink-0" /> {pt}
                          </li>
                        ))}
                      </ul>
                    )}
                    {s.note && <p className="text-xs text-bone/40 mt-5 italic">{s.note}</p>}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MORE THAN A TRADITIONAL SALE */}
      <section className="border-b border-bone/10" data-testid="more-than-sale-section">
        <div className="container-x py-20 md:py-28">
          <Reveal>
            <SectionLabel dark>More Than a Traditional Sale</SectionLabel>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold uppercase mt-6 mb-6">
              An alternative worth evaluating.
            </h2>
            <p className="text-sm text-bone/60 leading-relaxed max-w-2xl mb-14">
              For suitable properties, a structured development arrangement may offer landowners another
              option to consider before simply selling the property in its current state.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-bone/10">
            {moreThanSale.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06} className="border-b border-r border-bone/10">
                <div className="p-8 md:p-10 h-full" data-testid={`sale-card-${i + 1}`}>
                  <h3 className="text-base font-semibold uppercase tracking-tight mb-4">{c.title}</h3>
                  <p className="text-sm text-bone/60 leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="text-xs text-bone/40 mt-8 max-w-2xl leading-relaxed">
              A structured arrangement is an alternative to evaluate — not a guarantee of a better
              financial outcome than a traditional sale.
            </p>
          </Reveal>
        </div>
      </section>

      {/* IDEAL PROPERTY PROFILE */}
      <section className="border-b border-bone/10" data-testid="indicators-section">
        <div className="container-x py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel dark>Ideal Property Profile</SectionLabel>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold uppercase mt-6">
                Does your land have potential?
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {indicators.map((ind) => (
                  <li key={ind} className="flex items-start gap-3 text-sm text-bone/70 border-b border-bone/10 pb-4">
                    <Check size={14} className="text-taupe mt-0.5 shrink-0" /> {ind}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-bone/40 mt-8 leading-relaxed">
                These are indicators only. Every property must be evaluated individually, and no
                indicator guarantees development approval.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SUBMISSION FORM */}
      <section id="submit" className="scroll-mt-24" data-testid="submit-section">
        <div className="container-x py-20 md:py-28">
          <Reveal>
            <SectionLabel dark>Submit Your Property</SectionLabel>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-6 mb-12">
              <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold uppercase lg:col-span-7">
                Submit Your Property
              </h2>
              <p className="text-sm text-bone/60 leading-relaxed lg:col-span-5">
                If you own a property that has development potential, tell us about it. Every submission
                is reviewed with discretion and there is no obligation on either side.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="text-ink">
              <PropertyForm />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-xs text-bone/40 mt-10 max-w-3xl leading-relaxed">
              Nothing on this page constitutes an offer to sell securities, an investment recommendation,
              or a guarantee of profit, development approval or property value. Potential development
              arrangements are subject to due diligence, development feasibility, financing, legal
              documentation, tax considerations, professional advice and agreement between all parties.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default InvestWithUs;
